import {PromoWeekday} from "../../../apis/requests/promos/types";
import {PROMO_WEEKDAYS, StartMode, WEEKDAY_SHORT_LABELS} from "./constants";

const DATE_LENGTH = "YYYY-MM-DD".length;
const DAYS_IN_WEEK = 7;
export const START_OF_DAY = "T00:00:00";

type StartFields = {
  start_mode: StartMode;
  starts_at: string;
  start_weekday: PromoWeekday;
};

export const toDateInput = (isoDateTime: string | null): string => (isoDateTime ?? "").slice(0, DATE_LENGTH);

export const todayDateInput = (): string => toDateInput(new Date().toISOString());

const weekdayIndex = (weekday: PromoWeekday): number => (PROMO_WEEKDAYS.indexOf(weekday) + 1) % DAYS_IN_WEEK;

const parseDateInput = (date: string): Date => new Date(`${date}${START_OF_DAY}`);

const formatDateInput = (date: Date): string => {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
};

export const nextWeekdayDate = (weekday: PromoWeekday, fromDate: string): string => {
  const date = parseDateInput(fromDate);
  const daysAhead = (weekdayIndex(weekday) - date.getDay() + DAYS_IN_WEEK) % DAYS_IN_WEEK;
  date.setDate(date.getDate() + daysAhead);
  return formatDateInput(date);
};

const fallsOnWeekday = (date: string, weekday: PromoWeekday): boolean =>
  Boolean(date) && parseDateInput(date).getDay() === weekdayIndex(weekday);

export const effectiveStartDate = (values: StartFields, today: string): string => {
  if (values.start_mode === "date") return values.starts_at;
  if (fallsOnWeekday(values.starts_at, values.start_weekday)) return values.starts_at;
  return nextWeekdayDate(values.start_weekday, today);
};

const START_DAY_DETAIL = "Start";
const MONTH_DAY_FORMAT: Intl.DateTimeFormatOptions = {month: "short", day: "numeric"};

export type PromoDayLabel = {title: string; detail: string};

const weekdayOf = (date: Date): PromoWeekday => PROMO_WEEKDAYS[(date.getDay() + DAYS_IN_WEEK - 1) % DAYS_IN_WEEK];

const relativeDayDetail = (day: number): string => (day === 0 ? START_DAY_DETAIL : `Day ${day}`);

export const promoDayLabels = (values: StartFields, today: string, dayCount: number): PromoDayLabel[] => {
  const startDate = effectiveStartDate(values, today);
  return Array.from({length: dayCount}, (_, day) => {
    if (!startDate) return {title: `Day ${day}`, detail: day === 0 ? START_DAY_DETAIL : ""};
    const date = parseDateInput(startDate);
    date.setDate(date.getDate() + day);
    return {
      title: WEEKDAY_SHORT_LABELS[weekdayOf(date)],
      detail:
        values.start_mode === "date" ? date.toLocaleDateString("en-US", MONTH_DAY_FORMAT) : relativeDayDetail(day),
    };
  });
};
